import type { IntendedDisplay, SmartSort } from '@iheartjane/dm-sdk';
import type {
  BannerCarouselSlide,
  BannerCustomImageAdSlide,
  CustomImageAdProduct,
  WebCustomImageAdEvent,
} from '@iheartjane/menu';
import type { StoreCarouselBanner } from '@iheartjane/types';
import sortBy from 'lodash-es/sortBy';

interface BuildCustomImageAdSlideProps {
  instance: SmartSort<CustomImageAdProduct> | undefined;
  intendedDisplay: IntendedDisplay;
  onEvent?: (event: WebCustomImageAdEvent) => void;
  onImageClick?: (brandName: string) => void;
  toPdpUrl: (product: CustomImageAdProduct) => string;
}

export const buildCustomImageAdSlide = ({
  instance,
  intendedDisplay,
  onEvent,
  onImageClick,
  toPdpUrl,
}: BuildCustomImageAdSlideProps): BannerCustomImageAdSlide | undefined => {
  if (!instance?.products.length) return undefined;

  const customImageData = instance.customImages.find(
    (image) => image.intendedDisplay === intendedDisplay
  );

  if (!customImageData) return undefined;

  return {
    customImageData,
    id: 'carousel-image-ad',
    kind: 'customImageAd',
    onEvent,
    onImageClick,
    products: instance.products.map((product) => ({
      ...product.attributes,
      clickHandler: () => void product.click(),
      pdpUrl: toPdpUrl(product.attributes),
    })),
  };
};

export const getMobileCarouselSlides = (
  mobileCarouselSlides: StoreCarouselBanner[],
  desktopCarouselSlides: StoreCarouselBanner[]
) => {
  if (!mobileCarouselSlides) {
    return desktopCarouselSlides;
  }

  const missingSlides = desktopCarouselSlides.filter(
    (slideA) =>
      !mobileCarouselSlides.some(
        (slideB) => slideB.position === slideA.position
      )
  );

  return sortBy([...missingSlides, ...mobileCarouselSlides], 'position');
};

export const toBannerCarouselSlides = (
  slides: StoreCarouselBanner[],
  storeName?: string
): BannerCarouselSlide[] =>
  slides.map((slide) => ({
    id: String(slide.id),
    kind: 'image',
    imageAlt: slide.alt_text ?? `${storeName} Marketing Banner`,
    imageSrc: slide.image_url,
    url: slide.url,
  }));

export const storeHasVisibleCarousel = (store: {
  carousel_banner?: readonly StoreCarouselBanner[] | null;
}) =>
  store?.carousel_banner &&
  store?.carousel_banner.length > 0 &&
  store?.carousel_banner.every((slide) => slide.visible);
